import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { StyledTableCell, StyledTableRow } from '../../../components/StyledTableComponents';
import { TPaymentTerminalProvider } from '../schemas/payment-provider-schema';

type ProvidersListProps = {
  providers: TPaymentTerminalProvider[];
  onEdit: (provider: TPaymentTerminalProvider) => void;
  onDelete?: (provider: TPaymentTerminalProvider) => void;
  onToggleEnabled: (provider: TPaymentTerminalProvider) => void;
  togglingId?: string | null;
};

const isProviderRegistered = (provider: TPaymentTerminalProvider) => {
  return Boolean(provider.globalFields && Object.keys(provider.globalFields).length > 0);
};

const countScope = (provider: TPaymentTerminalProvider, scope: 'global' | 'client' | 'device') =>
  (provider.schema?.fields || []).filter((f) => (f.scope ?? 'global') === scope).length;

const ProvidersList = ({
  providers,
  onEdit,
  onDelete,
  onToggleEnabled,
  togglingId,
}: ProvidersListProps) => {
  if (!providers || providers.length === 0) {
    return (
      <Box sx={styles.emptyContainer}>
        <Typography sx={styles.emptyText}>No payment providers registered yet.</Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={styles.tableContainer}>
      <Table sx={{ minWidth: 800 }}>
        <TableHead>
          <StyledTableRow>
            <StyledTableCell>Provider</StyledTableCell>
            <StyledTableCell>Provider ID</StyledTableCell>
            <StyledTableCell>Fields</StyledTableCell>
            <StyledTableCell>Registration</StyledTableCell>
            <StyledTableCell>Status</StyledTableCell>
            <StyledTableCell>Updated</StyledTableCell>
            <StyledTableCell align="right">Actions</StyledTableCell>
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {providers.map((provider) => {
            const registered = isProviderRegistered(provider);
            const globalCount = countScope(provider, 'global');
            const clientCount = countScope(provider, 'client');
            const deviceCount = countScope(provider, 'device');
            return (
              <StyledTableRow key={provider.providerId}>
                <StyledTableCell sx={styles.providerCell}>
                  {provider.displayName || provider.providerId}
                </StyledTableCell>
                <StyledTableCell sx={styles.monoCell}>{provider.providerId}</StyledTableCell>
                <StyledTableCell>
                  <Box sx={styles.scopeChips}>
                    <Chip
                      size="small"
                      label={`G ${globalCount}`}
                      sx={{ ...styles.scopeChip, backgroundColor: '#dbeafe', color: '#1e40af' }}
                    />
                    <Chip
                      size="small"
                      label={`C ${clientCount}`}
                      sx={{ ...styles.scopeChip, backgroundColor: '#dcfce7', color: '#166534' }}
                    />
                    <Chip
                      size="small"
                      label={`D ${deviceCount}`}
                      sx={{ ...styles.scopeChip, backgroundColor: '#fef3c7', color: '#92400e' }}
                    />
                  </Box>
                </StyledTableCell>
                <StyledTableCell>
                  {registered ? (
                    <Chip
                      label="Registered"
                      size="small"
                      sx={{
                        backgroundColor: '#e8f5e9',
                        color: '#2e7d32',
                        fontFamily: 'GilroySemiBold',
                        fontSize: '11px',
                      }}
                    />
                  ) : (
                    <Chip
                      label="Not Registered"
                      size="small"
                      sx={{
                        backgroundColor: '#fff3e0',
                        color: '#e65100',
                        fontFamily: 'GilroySemiBold',
                        fontSize: '11px',
                      }}
                    />
                  )}
                </StyledTableCell>
                <StyledTableCell>
                  <Tooltip
                    title={
                      !registered
                        ? 'Set global fields first'
                        : provider.isEnabled
                          ? 'Disable provider'
                          : 'Enable provider'
                    }
                  >
                    <span>
                      <Switch
                        size="small"
                        checked={!!provider.isEnabled}
                        onChange={() => onToggleEnabled(provider)}
                        disabled={!registered || togglingId === provider.providerId}
                        sx={styles.switch}
                      />
                    </span>
                  </Tooltip>
                </StyledTableCell>
                <StyledTableCell sx={styles.dateCell}>
                  {provider.updatedAt ? new Date(provider.updatedAt).toLocaleString() : '-'}
                </StyledTableCell>
                <StyledTableCell align="right">
                  <Box sx={styles.actionBox}>
                    <Tooltip title="Edit schema & global values">
                      <IconButton size="small" onClick={() => onEdit(provider)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {onDelete && (
                      <Tooltip title="Delete provider">
                        <IconButton
                          size="small"
                          onClick={() => onDelete(provider)}
                          sx={{ color: '#dc2626' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </StyledTableCell>
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ProvidersList;

const styles = {
  tableContainer: {
    boxShadow: 'none',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  emptyContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    borderRadius: '8px',
    height: '6rem',
  },
  emptyText: {
    color: '#999',
    fontSize: '14px',
    fontFamily: 'InterRegular',
  },
  providerCell: {
    fontFamily: 'GilroySemiBold',
  },
  monoCell: {
    fontFamily: 'monospace',
    fontSize: 13,
    color: '#555',
  },
  scopeChips: {
    display: 'flex',
    gap: 0.5,
  },
  scopeChip: {
    fontFamily: 'GilroyBold',
    fontSize: 10,
    height: 20,
  },
  dateCell: {
    whiteSpace: 'nowrap' as const,
    color: '#555',
    fontSize: 13,
  },
  switch: {
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: '#4caf50',
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#4caf50',
    },
  },
  actionBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 0.25,
  },
};
